import { useState, useEffect } from 'react';
import { getUserPortfolio, normalizePortfolio, deletePortfolioMedia } from '../../services/portfolioService';
import PortfolioImageModal from './PortfolioImageModal';

/**
 * Portfolio - Componente unificado para mostrar el portfolio completo del usuario
 * Incluye portfolioSections y portfolioMedia
 */
const Portfolio = ({ userId, isOwnProfile = false }) => {
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    const loadPortfolio = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      const result = await getUserPortfolio(userId);

      if (result.success) {
        const normalized = normalizePortfolio(result.portfolioSections, result.portfolioMedia);
        setPortfolio(normalized);
      } else {
        setError(result.error);
      }

      setLoading(false);
    };

    loadPortfolio();
  }, [userId]);

  // Loading state
  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
        <p className="text-gray-600 dark:text-gray-400">Cargando portfolio...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-16">
        <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-red-100 to-orange-100 dark:from-red-900/30 dark:to-orange-900/30 rounded-full flex items-center justify-center">
          <svg className="w-12 h-12 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Error al cargar el portfolio
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
      </div>
    );
  }

  // Empty state
  if (!portfolio || (!portfolio.media.length && Object.keys(portfolio.sections).length === 0)) {
    return (
      <div className="text-center py-16">
        <div className="max-w-md mx-auto">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-purple-100 to-indigo-100 dark:from-purple-900/30 dark:to-indigo-900/30 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Sin portfolio aún
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Este usuario aún no ha agregado información a su portfolio.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Portfolio Media Gallery */}
      {portfolio.media.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3">
          {portfolio.media.map((item, index) => (
            <div
              key={item.id}
              onClick={() => setSelectedImageIndex(index)}
              className="group relative bg-gray-100 dark:bg-slate-700 rounded-lg overflow-hidden aspect-square cursor-pointer hover:shadow-xl transition-all duration-300"
            >
              {item.url && (
                <img
                  src={item.url}
                  alt={item.title || 'Portfolio item'}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
              )}

              {/* Botón borrar (solo propio perfil) */}
              {isOwnProfile && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!window.confirm('¿Eliminar esta imagen del portfolio?')) return;
                    setDeletingId(item.id);
                    deletePortfolioMedia(userId, item.id, item.url).then((result) => {
                      setDeletingId(null);
                      if (result.success) {
                        setPortfolio((prev) => ({
                          ...prev,
                          media: prev.media.filter((m) => m.id !== item.id)
                        }));
                        setSelectedImageIndex((i) => {
                        if (i === null) return null;
                        if (i === index) return index > 0 ? index - 1 : 0;
                        if (i > index) return i - 1;
                        return i;
                      });
                      } else {
                        alert(result.error || 'No se pudo eliminar.');
                      }
                    });
                  }}
                  disabled={deletingId === item.id}
                  className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-red-600 text-white transition-colors z-10 disabled:opacity-50"
                  title="Eliminar imagen"
                >
                  {deletingId === item.id ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  )}
                </button>
              )}
              
              {/* Overlay con información */}
              {item.title && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-2">
                  <div>
                    <h3 className="text-white font-semibold text-xs">{item.title}</h3>
                    {item.description && (
                      <p className="text-white/80 text-[10px] line-clamp-2">{item.description}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal de imagen */}
      {selectedImageIndex !== null && portfolio.media.length > 0 && (
        <PortfolioImageModal
          images={portfolio.media}
          currentIndex={selectedImageIndex}
          onClose={() => setSelectedImageIndex(null)}
          onNavigate={setSelectedImageIndex}
        />
      )}

      {/* Experience Section */}
      {portfolio.experience && portfolio.experience.length > 0 && (
        <PortfolioSection
          title="Experiencia"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          }
          items={portfolio.experience}
        />
      )}

      {/* Education Section */}
      {portfolio.education && portfolio.education.length > 0 && (
        <PortfolioSection
          title="Educación"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M12 14l9-5-9-5-9 5 9 5z" />
              <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
            </svg>
          }
          items={portfolio.education}
        />
      )}

      {/* Skills Section */}
      {portfolio.skills && portfolio.skills.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
            <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
            Habilidades
          </h2>
          <div className="flex flex-wrap gap-2">
            {portfolio.skills.map((skill, index) => (
              <span
                key={index}
                className="px-4 py-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-sm font-medium rounded-full"
              >
                {typeof skill === 'string' ? skill : skill.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Projects Section */}
      {portfolio.projects && portfolio.projects.length > 0 && (
        <PortfolioSection
          title="Proyectos"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
          }
          items={portfolio.projects}
        />
      )}

      {/* Certifications Section */}
      {portfolio.certifications && portfolio.certifications.length > 0 && (
        <PortfolioSection
          title="Certificaciones"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
          }
          items={portfolio.certifications}
        />
      )}

      {/* Languages Section */}
      {portfolio.languages && portfolio.languages.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
            <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
            </svg>
            Idiomas
          </h2>
          <div className="space-y-3">
            {portfolio.languages.map((language, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-gray-900 dark:text-white font-medium">
                  {typeof language === 'string' ? language : language.name}
                </span>
                {language.level && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {language.level}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Awards Section */}
      {portfolio.awards && portfolio.awards.length > 0 && (
        <PortfolioSection
          title="Premios y Reconocimientos"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          }
          items={portfolio.awards}
        />
      )}
    </div>
  );
};

/**
 * PortfolioSection - Componente reutilizable para secciones del portfolio
 */
const PortfolioSection = ({ title, icon, items }) => {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
        <span className="text-indigo-600 dark:text-indigo-400">{icon}</span>
        {title}
      </h2>
      
      <div className="space-y-6">
        {items.map((item, index) => (
          <div key={index} className="border-l-4 border-indigo-600 dark:border-indigo-400 pl-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {item.title || item.name || item.position}
            </h3>
            
            {(item.company || item.institution || item.school) && (
              <p className="text-indigo-600 dark:text-indigo-400 font-medium">
                {item.company || item.institution || item.school}
              </p>
            )}
            
            {(item.startDate || item.date) && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {item.startDate} {item.endDate && `- ${item.endDate}`}
                {!item.endDate && item.startDate && ' - Presente'}
                {item.date && !item.startDate && item.date}
              </p>
            )}
            
            {item.description && (
              <p className="text-gray-600 dark:text-gray-400 mt-2 whitespace-pre-line">
                {item.description}
              </p>
            )}
            
            {item.technologies && item.technologies.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {item.technologies.map((tech, techIndex) => (
                  <span
                    key={techIndex}
                    className="px-2 py-1 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 text-xs rounded"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Portfolio;
